import React, { useEffect, useState } from "react";
import { Box } from "@mui/material";
import { Grid } from "@material-ui/core";
import CustomInput from "../../components/common/inputField";
import CustomModal from "../../components/common/customModal";
import { addBudgetFormData } from "../../types/adminSection";
import { showToast } from "../../components/common/toastNotification";
import adminServices from "../../services/adminServices";
import AutoCompleteUserSelect, {
  UserOption,
} from "../../components/AutoCompleteUserSelect/AutoCompleteUserSelect";
import dashboardService from "../../services/DashboardServices";

interface AddBudgetModalProps {
  open: boolean;
  onClose: () => void;
  onSave: () => void;
}

const AddBudgetModal: React.FC<AddBudgetModalProps> = ({
  open,
  onClose,
  onSave,
}) => {
  const [formData, setFormData] = useState<addBudgetFormData>({
    id: null,
    employeeName: "",
    userId: "",
    employeeCode: "",
    department: "",
    divisionName: "",
    functionName: "",
    designationName: "",
    hrManagerName: "",
    budget: "",
    crossFunctionalBudget: "",
  });
  const [selectedUserByName, setSelectedUserByName] =
    useState<UserOption | null>(null);
  const [formErrors, setFormErrors] = useState<Partial<addBudgetFormData>>({
    budget: "",
    crossFunctionalBudget: "",
  });
  const [btnLoading, setBtnLoading] = useState(false);

  useEffect(() => {
    const fetchEmpDetails = async () => {
      if (selectedUserByName && selectedUserByName.id) {
        try {
          const response = await dashboardService.dashboardKeySearchAPI({
            search: selectedUserByName.id,
          });

          // Check response and data array
          if (
            response?.data?.success &&
            Array.isArray(response.data.data) &&
            response.data.data.length > 0
          ) {
            const data = response.data.data[0];

            setFormData((prev) => ({
              ...prev,
              employeeName: data.fullName || "",
              userId: data.userId || "",
              employeeCode: data.userId || "",
              divisionName: data.division || "",
              department: data.department || "",
              designationName: data.designation || "",
              hrManagerName: data.managerName || "",
            }));
          }
        } catch (err) {
          // handle error if needed
        }
      }
    };

    fetchEmpDetails();
  }, [selectedUserByName]);

  const handleChange = (
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const { name, value } = event.target;

    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));

    setFormErrors((prevErrors) => ({
      ...prevErrors,
      [name]: "",
    }));
  };
  const handleModalClose = () => {
    setFormData({
      id: null,
      employeeName: "",
      userId: "",
      employeeCode: "",
      department: "",
      divisionName: "",
      functionName: "",
      designationName: "",
      hrManagerName: "",
      budget: "",
      crossFunctionalBudget: "",
    });
    setSelectedUserByName(null);
    setFormErrors({ budget: "", crossFunctionalBudget: "" });
    onClose();
  };

  const validateForm = () => {
    const errors: Partial<addBudgetFormData> = {};
    if (!formData.employeeName?.trim()) {
      errors.employeeName = "Employee Name is required";
    }
    if (!formData.budget?.trim()) {
      errors.budget = "Budget is required";
    }

    if (!formData.crossFunctionalBudget?.trim()) {
      errors.crossFunctionalBudget = "Cross Functional Budget is required";
    }

    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSaveData = async () => {
    if (!validateForm()) return;
    setBtnLoading(true);

    // Build payload as per API requirements
    const payload = {
      name: formData.employeeName?.trim() || null,
      eCode: formData.employeeCode?.trim() || null,
      divisionName: formData.divisionName?.trim() || null,
      functionName: formData.department?.trim() || null,
      designationName: formData.designationName?.trim() || null,
      hrManagerName: formData.hrManagerName?.trim() || null,
      allocatedIndividualBudget:
        formData.budget && !isNaN(Number(formData.budget))
          ? Number(formData.budget)
          : null,
      allocatedCrossFunctionalBudget:
        formData.crossFunctionalBudget &&
        !isNaN(Number(formData.crossFunctionalBudget))
          ? Number(formData.crossFunctionalBudget)
          : null,
    };

    try {
      const response = await adminServices.addBudgetDataAPI(payload);

      if (response?.data?.success) {
        showToast("Budget added successfully", "success");
        onSave();
        onClose();
        setFormData({
          id: null,
          employeeName: "",
          userId: "",
          employeeCode: "",
          department: "",
          divisionName: "",
          functionName: "",
          designationName: "",
          hrManagerName: "",
          budget: "",
          crossFunctionalBudget: "",
        });
        setSelectedUserByName(null);
      } else {
        showToast(response?.data?.message || "Something went wrong", "error");
      }
    } catch (error) {
      console.error("API error:", error);
    } finally {
      setBtnLoading(false);
    }
  };

  return (
    <CustomModal
      open={open}
      handleClose={handleModalClose}
      onSave={handleSaveData}
      title="Add Budget"
      saveButtonName="Submit"
      width={800}
      btnLoading={btnLoading}
    >
      <Box>
        <Grid container spacing={2}>
          <Grid item xs={6}>
            <AutoCompleteUserSelect
              label="Employee Name"
              placeholder="Enter Employee Name"
              value={selectedUserByName}
              backgroundColor="transparent"
              onChange={(user) => {
                setSelectedUserByName(user);
                setFormErrors((prev) => ({
                  ...prev,
                  employeeName: "",
                }));
              }}
              error={formErrors.employeeName ?? undefined}
              required
            />
          </Grid>
          <Grid item xs={6}>
            <CustomInput
              label="Employee Code"
              name="employeeCode"
              placeholder="Enter Employee Code"
              value={formData.employeeCode}
              onChange={handleChange}
              readOnly
              // error={formErrors.employeeCode}
              required
            />
          </Grid>
          <Grid item xs={6}>
            <CustomInput
              label="Division Name"
              name="divisionName"
              placeholder="Enter Division Name"
              value={formData.divisionName}
              onChange={handleChange}
              readOnly
              // error={formErrors.divisionName}
              required
            />
          </Grid>
          <Grid item xs={6}>
            <CustomInput
              label="Function Name"
              name="functionName"
              placeholder="Enter Function Name"
              value={formData.department}
              onChange={handleChange}
              readOnly
              // error={formErrors.department}
              required
            />
          </Grid>
          <Grid item xs={6}>
            <CustomInput
              label="Designation Name"
              name="designationName"
              placeholder="Enter Designation Name"
              value={formData.designationName}
              onChange={handleChange}
              readOnly
              // error={formErrors.designationName}
              required
            />
          </Grid>
          <Grid item xs={6}>
            <CustomInput
              label="HR Manager Name"
              name="hrManagerName"
              placeholder="Select HR Manager Name"
              value={formData.hrManagerName}
              onChange={handleChange}
              readOnly
              // error={formErrors.hrManagerName}
              required
            ></CustomInput>
          </Grid>
          <Grid item xs={6}>
            <CustomInput
              label="Budget (Individual/ Team)"
              name="budget"
              placeholder="Enter Budget (Individual/ Team)"
              value={formData.budget}
              onChange={handleChange}
              error={formErrors.budget ?? undefined}
              required
            />
          </Grid>
          <Grid item xs={6}>
            <CustomInput
              label="Cross Functional Budget"
              name="crossFunctionalBudget"
              placeholder="Enter Cross Functional Budget"
              value={formData.crossFunctionalBudget}
              onChange={handleChange}
              error={formErrors.crossFunctionalBudget ?? undefined}
              required
            />
          </Grid>
        </Grid>
      </Box>
    </CustomModal>
  );
};

export default AddBudgetModal;
